/* eslint-disable max-len */
import React, { useState, createContext, useEffect, useCallback } from 'react';
import * as api from '../api';

export const BookingContext = createContext();

export const BookingProvider = (props) => {
  const { children } = props;

  const [unfilteredBooking, setUnfilteredBooking] = useState();
  const [inputData, setInputData] = useState();
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState();
  const [activeJourneyIndex, setActiveJourneyIndex] = useState(-1);
  const [activePassengers, setActivePassengers] = useState([]);
  const [passengerIdentifier, setPassengerIdentifier] = useState('');
  const [autoCheckin, setAutoCheckin] = useState(false);
  const [autoCheckinCompleted, setAutoCheckinCompleted] = useState(false);

  const checkBookingClass = (journey) => {
    const notValidBookingClasses = ['G'];
    return journey.passengers.some((p) => notValidBookingClasses.includes(p.bookingClass));
  };

  const filterBooking = useCallback(
    () => (unfilteredBooking && {
      ...unfilteredBooking,
      primeJourney: unfilteredBooking.primeJourney.map((journey) => (
        {
          ...journey,
          passengers: journey.passengers.filter((_x, i) => activePassengers[i]),
        })),
      alternateJourneys: unfilteredBooking.alternateJourneys.map(
        (flight) => flight.map((journey) => (
          {
            ...journey,
            passengers: journey.passengers.filter((_x, i) => activePassengers[i]),
          }
        )),
      ),
    }
    ),
    [unfilteredBooking, activePassengers],
  );

  useEffect(() => {
    if (unfilteredBooking && activePassengers.length === 0) {
      setActivePassengers(unfilteredBooking.primeJourney?.[0]?.passengers.map((pax) => {
        if (passengerIdentifier) {
          return pax.passengerIdentifier === passengerIdentifier;
        }
        return true;
      }));
    }
  }, [activePassengers.length, unfilteredBooking, passengerIdentifier]);

  const booking = filterBooking();
  if (booking) {
    const allJourneys = [];
    booking.primeJourney.forEach((segment) => {
      allJourneys.push([segment]);
    });

    booking.alternates.forEach((segment) => {
      allJourneys.push([segment]);
    });

    // Filter out journeys that exist in primejourney
    booking.alternateJourneys.forEach((journeys) => {
      const existsInAllJourneys = allJourneys.some(
        (journey) => journey[0]?.flightTime?.scheduledDeparture === journeys[0]?.flightTime?.scheduledDeparture,
      );
      if (!existsInAllJourneys) {
        journeys.forEach((segment) => {
          allJourneys.push([segment]);
        });
      }
    });

    booking.allJourneys = booking ? allJourneys : [];
  }

  const fetchBookingByPNRandPI = (pnr, pi) => {
    setBookingError();
    setBookingLoading(true);
    return api.get(
      `/dcsidc/cprIdentification/fromPNRandPI?PNR=${pnr}&PI=${pi}`,
      (b) => {
        if (b && b.primeJourney && b.primeJourney.length > 0) {
          if (checkBookingClass(b.primeJourney[0])) {
            setBookingError({ text: 'bookingClass-error', code: '38', type: 'cprIdentification' });
          } else {
            setUnfilteredBooking(b);
            setInputData({ PNRandPI: { pnr, pi } });
          }
        } else {
          setBookingError({ text: 'No customers found', code: '37', type: 'cprIdentification' });
        }
      },
      setBookingError,
      setBookingLoading(false),
    );
  };

  const fetchBookingByPNR = (pnr, surname) => {
    setBookingError();
    setBookingLoading(true);

    if (pnr === '' || !pnr) {
      return new Promise(() => {
        setBookingLoading(false);
      });
    }
    return api.get(
      `/dcsidc/cprIdentification/fromOnlyPNR?PNR=${pnr}`,
      (b) => {
        if (b && b.primeJourney && b.primeJourney.length > 0) {
          if (checkBookingClass(b.primeJourney[0])) {
            setBookingError({ text: 'bookingClass-error', code: '38', type: 'cprIdentification' });
          } else {
            setUnfilteredBooking(b);
            setInputData({ PNRAndName: { pnr, surname } });
          }
        } else {
          setBookingError({ text: 'No customers found', code: '37', type: 'cprIdentification' });
        }
      },
      setBookingError,
      setBookingLoading(false),
    );
  };

  const fetchBookingByPNRAndName = (pnr, surname) => {
    setBookingError();
    setBookingLoading(true);

    if (pnr === '' || surname === '' || !pnr || !surname) {
      return new Promise(() => {
        setBookingLoading(false);
      });
    }

    // const regexNameWithSpecialChars = /[ \-_*]/;
    // if (regexNameWithSpecialChars.test(surname)) {
    //   return fetchBookingByPNR(pnr, surname);
    // }

    return api.get(
      `/dcsidc/cprIdentification/fromPNRAndName?PNR=${pnr}&surname=${surname}`,
      (b) => {
        if (b && b.primeJourney && b.primeJourney.length > 0) {
          if (checkBookingClass(b.primeJourney[0])) {
            setBookingError({ text: 'bookingClass-error', code: '38', type: 'cprIdentification' });
          } else {
            setUnfilteredBooking(b);
            setInputData({ PNRAndName: { pnr, surname } });
          }
        } else {
          setBookingError({ text: 'No customers found', code: '37', type: 'cprIdentification' });
        }
      },
      setBookingError,
      setBookingLoading(false),
    );
  };

  const fetchBookingByTicketAndName = (ticket, surname) => {
    setBookingError();
    setBookingLoading(true);
    return api.get(
      `/dcsidc/cprIdentification/fromTicketAndName?ticket=${ticket}&surname=${surname}`,
      (b) => {
        if (b && b.primeJourney && b.primeJourney.length > 0) {
          if (checkBookingClass(b.primeJourney[0])) {
            setBookingError({ text: 'bookingClass-error', code: '38', type: 'cprIdentification' });
          } else {
            setUnfilteredBooking(b);
            setInputData({ ticketAndName: { ticket, surname } });
          }
        } else {
          setBookingError({ text: 'No customers found', code: '37', type: 'cprIdentification' });
        }
      },
      setBookingError,
      setBookingLoading(false),
    );
  };

  const refetchBooking = () => {
    if (inputData && inputData.ticketAndName) {
      // eslint-disable-next-line max-len
      return fetchBookingByTicketAndName(inputData.ticketAndName.ticket, inputData.ticketAndName.surname);
    }
    if (inputData && inputData.PNRAndName) {
      return fetchBookingByPNRAndName(inputData.PNRAndName.pnr, inputData.PNRAndName.surname);
      // if (!inputData.PNRAndName.surname || inputData.PNRAndName.surname.includes(' ')) {
      //   fetchBookingByPNR(inputData.PNRAndName.pnr.toUpperCase());
      // } else {
      //   return fetchBookingByPNRAndName(inputData.PNRAndName.pnr, inputData.PNRAndName.surname);
      // }
    } if (inputData && inputData.PNRandPI) {
      return fetchBookingByPNRandPI(inputData.PNRandPI.pnr, inputData.PNRandPI.pi);
    }
    const emptyPromise = Promise.resolve('');
    return emptyPromise;
  };

  const clearBooking = () => {
    setUnfilteredBooking();
    setInputData();
    setInputData();
  };

  const activeJourney = ((booking && activeJourneyIndex !== -1)
    && (activeJourneyIndex.primeJourney
      ? (booking.primeJourney && booking.primeJourney)
      : booking.alternateJourneys
      && booking.alternateJourneys[activeJourneyIndex.alternateJourneys.index]));

  const values = {
    booking,
    clearBooking,
    bookingError,
    bookingLoading,
    refetchBooking,
    activePassengers,
    setActivePassengers,
    fetchBookingByPNRAndName,
    fetchBookingByTicketAndName,
    fetchBookingByPNRandPI,
    fetchBookingByPNR,
    activeJourney,
    setActiveJourneyIndex,
    unfilteredBooking,
    setPassengerIdentifier,
    passengerIdentifier,
    setAutoCheckin,
    autoCheckin,
    autoCheckinCompleted,
    setAutoCheckinCompleted,
  };

  return (
    <BookingContext.Provider value={values}>
      {children}
    </BookingContext.Provider>
  );
};
